import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { lazy, Suspense, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import Chip from "@mui/material/Chip";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import CircularProgress from "@mui/material/CircularProgress";
import Alert from "@mui/material/Alert";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogActions from "@mui/material/DialogActions";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutlined";
import EditNoteIcon from "@mui/icons-material/EditNote";
import InsightsIcon from "@mui/icons-material/Insights";
import QuestionAnswerOutlinedIcon from "@mui/icons-material/QuestionAnswerOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import RecordVoiceOverOutlinedIcon from "@mui/icons-material/RecordVoiceOverOutlined";
import { toast } from "sonner";
import { useAuth } from "@/hooks/useAuth";
import { fetchJDById, deleteJD, jdsQueryKey, type JDRow } from "@/lib/queries/jds";
import { useApplications } from "@/context/ApplicationContext";
import { JobOverviewTab } from "@/components/jobs/JobOverviewTab";
import { JobQATab } from "@/components/jobs/JobQATab";
import { MatchTab } from "@/components/jobs/MatchTab";
import type { StageKey } from "@/lib/stages";

const MaterialsPanel = lazy(() =>
  import("./app.generate").then((m) => ({ default: m.MaterialsPanel })),
);
const InterviewPanel = lazy(() =>
  import("./app.interview").then((m) => ({ default: m.InterviewPanel })),
);

function TabFallback() {
  return (
    <Box sx={{ display: "grid", placeItems: "center", py: 6 }}>
      <CircularProgress />
    </Box>
  );
}

type JobSearch = { tab?: string; stage?: string };

export const Route = createFileRoute("/app/jobs/$jobId")({
  component: JobDetailPage,
  head: () => ({ meta: [{ title: "Job — JobYoda" }] }),
  validateSearch: (s: Record<string, unknown>): JobSearch => ({
    tab: typeof s.tab === "string" ? s.tab : undefined,
    stage: typeof s.stage === "string" ? s.stage : undefined,
  }),
});

const TAB_KEYS = ["overview", "match", "qa", "materials", "interview"] as const;
type TabKey = (typeof TAB_KEYS)[number];

function formatSalary(
  min: number | null,
  max: number | null,
  currency: string | null,
  period: string | null,
): string {
  const cur = currency?.trim() || "";
  const fmt = (n: number) => {
    if (n >= 1000) return `${Math.round(n / 1000)}k`;
    return String(n);
  };
  let range = "";
  if (min != null && max != null) range = `${fmt(min)}–${fmt(max)}`;
  else if (min != null) range = `${fmt(min)}+`;
  else if (max != null) range = `up to ${fmt(max)}`;
  const parts = [cur && `${cur} `, range, period && ` / ${period}`].filter(Boolean);
  return parts.join("").trim();
}

function JobDetailPage() {
  const { jobId } = Route.useParams();
  const { tab, stage: stageFromUrl } = Route.useSearch();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const userId = user?.id ?? null;
  const { applications, remove: removeApplication, currentStageFor } = useApplications();
  const [confirmDelete, setConfirmDelete] = useState(false);

  const activeTab: TabKey = (TAB_KEYS as readonly string[]).includes(tab ?? "")
    ? (tab as TabKey)
    : "overview";

  const { data: jd, isLoading, error } = useQuery({
    queryKey: ["job-detail", userId, jobId],
    queryFn: () => fetchJDById(userId as string, jobId),
    enabled: !!userId && !!jobId,
  });

  const application = useMemo(
    () => applications.find((a) => a.jdId === jobId) ?? null,
    [applications, jobId],
  );

  const currentStageKey: StageKey | null = useMemo(() => {
    if (!application) return null;
    const s = currentStageFor(application.id);
    return (s?.stage_key as StageKey | undefined) ?? null;
  }, [application, currentStageFor]);

  const deleteMutation = useMutation({
    mutationFn: () => deleteJD(userId as string, jobId),
    onSuccess: () => {
      if (application) removeApplication(application.id);
      if (userId) {
        queryClient.invalidateQueries({ queryKey: jdsQueryKey(userId) });
        queryClient.removeQueries({ queryKey: ["job-detail", userId, jobId] });
      }
      toast.success("Job deleted.");
      navigate({ to: "/app/jobs" });
    },
    onError: (e: unknown) => {
      toast.error(e instanceof Error ? e.message : "Failed to delete job.");
    },
  });

  const handleTabChange = (_: unknown, value: TabKey) => {
    navigate({
      to: "/app/jobs/$jobId",
      params: { jobId },
      search: { tab: value },
      replace: true,
    });
  };

  const switchTab = (value: TabKey) => handleTabChange(null, value);

  if (isLoading) {
    return (
      <Box sx={{ display: "grid", placeItems: "center", py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !jd) {
    return (
      <Box>
        <Button component={Link} to="/app/jobs" startIcon={<ArrowBackIcon />} sx={{ mb: 2 }}>
          Back to Jobs
        </Button>
        <Alert severity="error">Couldn't load this job.</Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Stack direction="row" spacing={1} sx={{ alignItems: "center", mb: 2 }}>
        <IconButton component={Link} to="/app/jobs" aria-label="Back">
          <ArrowBackIcon />
        </IconButton>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="h5" sx={{ fontWeight: 700 }} noWrap>
            {jd.role_title || "Untitled role"}
          </Typography>
          <Stack direction="row" spacing={1} sx={{ alignItems: "center", mt: 0.5, flexWrap: "wrap", rowGap: 0.5 }}>
            {jd.company && (
              <Typography variant="body2" color="text.secondary">
                {jd.company}
              </Typography>
            )}
            {jd.location && (
              <Chip size="small" variant="outlined" label={jd.location} />
            )}
            {jd.work_arrangement && (
              <Chip size="small" variant="outlined" color="info" label={jd.work_arrangement} />
            )}
            {(jd.salary_min != null || jd.salary_max != null) && (
              <Chip
                size="small"
                variant="outlined"
                color="success"
                label={formatSalary(jd.salary_min, jd.salary_max, jd.salary_currency, jd.salary_period)}
              />
            )}
            <Chip
              size="small"
              label={application ? application.status : "Saved"}
              color={application ? "primary" : "default"}
            />
          </Stack>
        </Box>
        <Button
          color="error"
          variant="outlined"
          size="small"
          startIcon={<DeleteOutlineIcon />}
          onClick={() => setConfirmDelete(true)}
          disabled={deleteMutation.isPending}
        >
          Delete
        </Button>
      </Stack>

      <Card sx={{ mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab value="overview" icon={<EditNoteIcon />} iconPosition="start" label="Overview" />
          <Tab value="match" icon={<InsightsIcon />} iconPosition="start" label="Success" />
          <Tab value="qa" icon={<QuestionAnswerOutlinedIcon />} iconPosition="start" label="Q&A" />
          <Tab value="materials" icon={<DescriptionOutlinedIcon />} iconPosition="start" label="Materials" />
          <Tab value="interview" icon={<RecordVoiceOverOutlinedIcon />} iconPosition="start" label="Interview" />
        </Tabs>
      </Card>

      <Box>
        {activeTab === "overview" && userId && (
          <JobOverviewTab
            jd={jd as JDRow}
            userId={userId}
            application={application}
            onSwitchTab={switchTab}
          />
        )}
        {activeTab === "match" && (
          <MatchTab
            jdId={jd.id}
            jdText={jd.jd_text}
            roleTitle={jd.role_title}
            company={jd.company}
          />
        )}
        {activeTab === "qa" && (
          <JobQATab
            jdId={jd.id}
            roleTitle={jd.role_title}
            company={jd.company}
            defaultStageKey={(stageFromUrl as StageKey | undefined) ?? currentStageKey}
          />
        )}
        {activeTab === "materials" && (
          <Suspense fallback={<TabFallback />}>
            <MaterialsPanel initialJdText={jd.jd_text} hideHeader />
          </Suspense>
        )}
        {activeTab === "interview" && (
          <Suspense fallback={<TabFallback />}>
            <InterviewPanel jdId={jd.id} hideHeader hideJdPicker defaultStageKey={currentStageKey} />
          </Suspense>
        )}
      </Box>

      <Dialog open={confirmDelete} onClose={() => setConfirmDelete(false)}>
        <DialogTitle>Delete this job?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This permanently removes <strong>{jd.role_title || "this job"}</strong>
            {jd.company ? ` at ${jd.company}` : ""} along with its success scores, generated materials, and
            cached Q&amp;A. Tracked application status will also be removed. This can't be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(false)} disabled={deleteMutation.isPending}>
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => deleteMutation.mutate()}
            disabled={deleteMutation.isPending}
            startIcon={deleteMutation.isPending ? <CircularProgress size={14} /> : <DeleteOutlineIcon />}
          >
            {deleteMutation.isPending ? "Deleting…" : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
